import React, { forwardRef, useEffect } from 'react';
import {
  Box,
  Radio,
  FormControl,
  FormControlLabel,
  FormHelperText,
  useTheme,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useFieldArray, Controller, useFormContext } from 'react-hook-form';
import { observer } from 'mobx-react';
import {
  DatePicker,
  FieldWrapperVerticalV2,
  ReadOnly,
  useLocalization,
} from '@ui-framework/index';
import { constantCase } from 'case-anything';
import { FormattedMessage } from 'react-intl';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import IconLibrary from '../IconLibrary';
import PermissionButton, { PermissionIconButton } from '../PermissionButton';
import { useAvailabilitySlotTypes } from '../../providers/AvailabilitySlotTypeProvider';
import { TimeSlotTypeChipWithAfterBeforeHours } from '../TimeSlotTypeChip';

// This component provides backward compatibility when we used to have after hours and before hours
// They will be displayed only in readonly mode with preference first given to slottypecode

const CustomerServiceRequests = forwardRef((props, ref) => {
  const { formatMessage } = useLocalization();
  const theme = useTheme();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext();

  const { slotTypes, getSlotByCode } = useAvailabilitySlotTypes();

  const maxFields = props?.field?.max || 3;
  const minWidth = props?.minWidth || '600px';

  const emptyField = {
    requestDate: null,
    slotTypeCode: slotTypes[0]?.code,
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: props.name,
  });

  useEffect(() => {
    if (fields.length === 0) {
      append({ ...emptyField });
    }
  }, [fields, fields.length, append]);

  const isWrite = props?.permission?.requestDate === 'WRITE';

  if (
    props?.permission?.requestDate === 'READ' &&
    Array.isArray(fields) &&
    (fields?.length === 0 || (fields.length === 1 && !fields[0].requestDate))
  ) {
    return (
      <ReadOnly>
        <FormattedMessage
          id="NO_SERVICE_REQUEST_DATES"
          defaultMessage="No service request dates"
        />
      </ReadOnly>
    );
  }

  const readOnlyValue = getValues(props?.name || 'undefined');

  return (
    <Box
      ref={ref}
      sx={{
        mt: 1,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.divider,
        borderRadius: 1,
        p: 1,
        minWidth: {
          md: minWidth,
        },
      }}>
      {fields.map((field, idx) => (
        <Grid2 container spacing={2} key={field.id} sx={{ mb: 1 }}>
          <Grid2 xs={isWrite ? 4 : 2}>
            <Controller
              control={control}
              name={`${props.name}.${idx}.requestDate`}
              render={({ field: controllerField }) => {
                const fieldError = {
                  [controllerField?.name]:
                    errors[props.name]?.[idx]?.requestDate,
                };
                return (
                  <FieldWrapperVerticalV2
                    key={controllerField?.name}
                    field={{
                      name: controllerField?.name,
                      label: props?.field?.dateComponentTitle
                        ? props?.field?.dateComponentTitle
                        : {
                            localeId: 'REQUESTED_DATE',
                            defaultMessage: 'Requested Date',
                          },
                      required: props?.field?.required || false,
                    }}
                    errors={fieldError}>
                    <DatePicker
                      {...controllerField}
                      disablePast
                      required
                      value={controllerField.value}
                      onChange={date => {
                        controllerField.onChange(date);
                      }}
                      permission={props?.permission?.requestDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          InputLabelProps: {
                            shrink: true,
                            sx: { m: '0 !important' },
                          },
                        },
                      }}
                    />
                  </FieldWrapperVerticalV2>
                );
              }}
            />
          </Grid2>
          <Grid2 xs={isWrite ? 7 : 4}>
            <Controller
              control={control}
              name={`${props?.name}.${idx}.slotTypeCode`}
              render={({ field: controllerField, fieldState: { error } }) => {
                const fieldError = {
                  [controllerField?.name]:
                    errors[props?.name]?.[idx]?.slotTypeCode,
                };
                const slotFound = getSlotByCode({
                  code: controllerField.value,
                });
                return (
                  <FieldWrapperVerticalV2
                    labelStyle={{ marginBottom: 0 }}
                    key={controllerField?.name}
                    field={{
                      name: controllerField?.name,
                      label: {
                        localeId: 'SLOT',
                        defaultMessage: 'Slot',
                      },
                      required: false,
                    }}
                    errors={fieldError}>
                    {props?.permission?.requestDate === 'WRITE' ? (
                      <Box
                        sx={{
                          py: 1,
                          ...(error && {
                            display: 'flex',
                            border: '1px solid',
                            pl: 1,
                            borderRadius: '8px',
                            borderColor: 'error.main',
                          }),
                        }}>
                        {slotTypes.map(item => {
                          return (
                            <FormControl key={item.code}>
                              <FormControlLabel
                                control={
                                  <Radio
                                    sx={{ py: 0 }}
                                    checked={
                                      controllerField.value === item.code
                                    }
                                    onChange={() => {
                                      controllerField.onChange(item.code);
                                    }}
                                  />
                                }
                                label={
                                  <FormattedMessage
                                    id={constantCase(item.name)}
                                    defaultMessage={item.name}
                                  />
                                }
                              />
                            </FormControl>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box>
                        <TimeSlotTypeChipWithAfterBeforeHours
                          name={slotFound?.name || slotFound?.code}
                          code={slotFound?.code}
                          afterHour={readOnlyValue[idx]?.afterHour}
                          beforeHour={readOnlyValue[idx]?.beforeHour}
                        />
                      </Box>
                    )}
                    <Box>
                      {error?.message && (
                        <FormHelperText error>{error.message}</FormHelperText>
                      )}
                    </Box>
                  </FieldWrapperVerticalV2>
                );
              }}
            />
          </Grid2>
          {props?.permission?.requestDate === 'WRITE' && (
            <Grid2 xs={1} mt={3}>
              <PermissionIconButton
                permissionName="DELETE_SERVICE_REQUESTED_IN_JOB"
                size="small"
                onClick={() => {
                  remove(idx);
                }}
                color="error">
                <Delete />
              </PermissionIconButton>
            </Grid2>
          )}
        </Grid2>
      ))}
      {fields.length < maxFields &&
        props?.permission?.requestDate === 'WRITE' && (
          <PermissionButton
            permissionName="ADD_SERVICE_REQUESTED_IN_JOB"
            variant="text"
            startIcon={<IconLibrary.Add fontSize="inherit" />}
            onClick={() => {
              append({ ...emptyField });
            }}
            sx={{ mt: 0 }}>
            {formatMessage({
              id: 'SERVICE_REQUESTED_DATE',
              defaultMessage: 'Service Requested Date',
            })}
          </PermissionButton>
        )}
    </Box>
  );
});

export const CustomerServiceRequest = forwardRef((props, ref) => {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
    getValues,
  } = useFormContext();

  const { slotTypes, getSlotByCode } = useAvailabilitySlotTypes();

  const minWidth = props?.minWidth || '600px';
  const currentValue = getValues(props?.name || 'undefined');

  const permission = props?.permission;
  const isWrite = permission === 'WRITE';

  const isDisabled = props?.disabled || false;

  if (permission === 'READ' && !currentValue?.requestDate) {
    return (
      <ReadOnly>
        <FormattedMessage
          id="NO_SERVICE_REQUEST_DATES"
          defaultMessage="No service request dates"
        />
      </ReadOnly>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        mt: 1,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.divider,
        borderRadius: 1,
        p: 1,
        minWidth: {
          md: minWidth,
        },
      }}>
      <Grid2 container spacing={2} sx={{ mb: 1 }}>
        <Grid2 xs={isWrite ? 4 : 2}>
          <Controller
            control={control}
            name={`${props.name}.requestDate`}
            render={({ field: controllerField }) => {
              const fieldError = {
                [controllerField?.name]: errors[props.name]?.requestDate,
              };
              return (
                <FieldWrapperVerticalV2
                  key={controllerField?.name}
                  field={{
                    name: controllerField?.name,
                    label: props?.field?.dateComponentTitle
                      ? props?.field?.dateComponentTitle
                      : {
                          localeId: 'REQUESTED_DATE',
                          defaultMessage: 'Requested Date',
                        },
                    required: props?.field?.required || false,
                  }}
                  errors={fieldError}>
                  <DatePicker
                    {...controllerField}
                    disablePast
                    required
                    disabled={isDisabled}
                    value={controllerField.value}
                    onChange={date => {
                      controllerField.onChange(date);
                    }}
                    permission={props?.permission?.requestDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        InputLabelProps: {
                          shrink: true,
                          sx: { m: '0 !important' },
                        },
                      },
                    }}
                  />
                </FieldWrapperVerticalV2>
              );
            }}
          />
        </Grid2>
        <Grid2 xs={isWrite ? 7 : 4}>
          <Controller
            control={control}
            name={`${props?.name}.slotTypeCode`}
            render={({ field: controllerField, fieldState: { error } }) => {
              const fieldError = {
                [controllerField?.name]: errors[props?.name]?.slotTypeCode,
              };
              const slotFound = getSlotByCode({
                code: controllerField.value,
              });
              return (
                <FieldWrapperVerticalV2
                  labelStyle={{ marginBottom: 0 }}
                  key={controllerField?.name}
                  field={{
                    name: controllerField?.name,
                    label: {
                      localeId: 'SLOT',
                      defaultMessage: 'Slot',
                    },
                    required: false,
                  }}
                  errors={fieldError}>
                  {isWrite ? (
                    <Box
                      sx={{
                        py: 1,
                        ...(error && {
                          display: 'flex',
                          border: '1px solid',
                          pl: 1,
                          borderRadius: '8px',
                          borderColor: 'error.main',
                        }),
                      }}>
                      {slotTypes.map(item => {
                        return (
                          <FormControl disabled={isDisabled} key={item.code}>
                            <FormControlLabel
                              control={
                                <Radio
                                  sx={{ py: 0 }}
                                  checked={controllerField.value === item.code}
                                  onChange={() => {
                                    controllerField.onChange(item.code);
                                  }}
                                />
                              }
                              label={item.name}
                            />
                          </FormControl>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box>
                      <TimeSlotTypeChipWithAfterBeforeHours
                        name={slotFound?.name || slotFound?.code}
                        code={slotFound?.code}
                        afterHour={currentValue?.afterHour}
                        beforeHour={currentValue?.beforeHour}
                      />
                    </Box>
                  )}
                  <Box>
                    {error?.message && (
                      <FormHelperText error>{error.message}</FormHelperText>
                    )}
                  </Box>
                </FieldWrapperVerticalV2>
              );
            }}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
});

export default observer(CustomerServiceRequests);
